/**
 * Session-resume (plans/session-resume.md): персистенция активной host/player-
 * сессии в `sessionStorage`, чтобы обновление вкладки (F5) возвращало пользователя
 * в тот же экран/фазу, а не на главную.
 *
 * Принцип:
 *  - сервер (`hub.go`) переживает пустую комнату до `roomIdleTTL` (10 мин);
 *  - при (ре)джойне сервер пушит полное состояние (фаза/вопрос/счёт/участники);
 *  - движок идемпотентен по playerID → реконнект игрока сохраняет счёт.
 *
 * Поэтому клиенту достаточно помнить {pin, sessionId} (хост) / {pin, nick} (игрок)
 * и восстановить экран при загрузке — а WS-слой (`useQuizSocket`) сам подключится,
 * отправит `join` и регидратирует UI.
 *
 * TTL-сторож (10 мин) синхронен с серверным `roomIdleTTL`: запись старше TTL
 * отбрасывается, чтобы не пытаться восстановить уже убранную сервером комнату.
 */
const HOST_KEY = 'kikayoo.activeHost';
const PLAYER_KEY = 'kikayoo.activePlayer';
const TTL_MS = 10 * 60 * 1000;

interface Stamped {
  ts: number;
}

function readStamped<T extends Stamped>(key: string): T | null {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as T;
    if (typeof parsed?.ts !== 'number') return null;
    if (Date.now() - parsed.ts > TTL_MS) {
      // Истекла — комната на сервере уже убрана; не пытаемся восстановить.
      window.sessionStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStamped(key: string, value: Record<string, unknown>): void {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ ...value, ts: Date.now() }));
  } catch {
    // private mode / quota — silently skip (resume просто не сработает, как раньше).
  }
}

function removeKey(key: string): void {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export interface HostSessionRecord {
  pin: string;
  sessionId: number;
}

export interface PlayerSessionRecord {
  pin: string;
  nick: string;
}

// ─── Host ────────────────────────────────────────────────────────────────────

/** Сохранить активную host-сессию (вызывается при успешном старте сессии). */
export function saveHost(pin: string, sessionId: number): void {
  writeStamped(HOST_KEY, { pin, sessionId });
}

/** Вернуть активную host-сессию, если она валидна (моложе TTL); иначе null. */
export function loadHost(): HostSessionRecord | null {
  const v = readStamped<HostSessionRecord & Stamped>(HOST_KEY);
  if (!v || typeof v.pin !== 'string' || typeof v.sessionId !== 'number') return null;
  return { pin: v.pin, sessionId: v.sessionId };
}

/** Стереть запись host-сессии (выход/завершение). */
export function clearHost(): void {
  removeKey(HOST_KEY);
}

// ─── Player ──────────────────────────────────────────────────────────────────

/** Сохранить активную player-сессию (вызывается при входе в игру с ником). */
export function savePlayer(pin: string, nick: string): void {
  writeStamped(PLAYER_KEY, { pin, nick });
}

/** Вернуть активную player-сессию, если она валидна (моложе TTL); иначе null. */
export function loadPlayer(): PlayerSessionRecord | null {
  const v = readStamped<PlayerSessionRecord & Stamped>(PLAYER_KEY);
  if (!v || typeof v.pin !== 'string' || typeof v.nick !== 'string') return null;
  return { pin: v.pin, nick: v.nick };
}

/** Стереть запись player-сессии (выход/завершение). */
export function clearPlayer(): void {
  removeKey(PLAYER_KEY);
}

// ─── Настройки старта сессии (Фаза 13, Эпик C2) ───────────────────────────────

const START_OPTIONS_KEY = 'kikayoo.startOptions';

/** Настройки проведения, выбранные в модале «Провести» кабинета (/me). */
export interface StartOptionsRecord {
  scorePause: boolean;
  lockAfterStart: boolean;
  strictNick: boolean;
  /** Авто-переход к следующему вопросу после reveal (Фаза 16, A). */
  autoNext: boolean;
}

/**
 * Сохранить настройки старта (кнопка «Провести» в модале кабинета). Читает их
 * лобби ведущего ([`HostSession`](./screens/HostSession.tsx)) при mount —
 * предзаполняет чекбоксы; финальная точка истины — лобби, где хост может
 * изменить значения до отправки `start`. TTL тот же, что у активных сессий.
 */
export function saveStartOptions(options: StartOptionsRecord): void {
  writeStamped(START_OPTIONS_KEY, { ...options });
}

/** Последние выбранные настройки старта, если запись жива (TTL); иначе null. */
export function loadStartOptions(): StartOptionsRecord | null {
  const v = readStamped<StartOptionsRecord & Stamped>(START_OPTIONS_KEY);
  if (!v) return null;
  // autoNext (Фаза 16, A) добавлен позже остальных: записи старого формата без
  // него валидны — флаг читается как false (back-compat: запись могла быть
  // сохранена страницей предыдущего релиза до редеплоя).
  if (
    typeof v.scorePause !== 'boolean' ||
    typeof v.lockAfterStart !== 'boolean' ||
    typeof v.strictNick !== 'boolean' ||
    (v.autoNext !== undefined && typeof v.autoNext !== 'boolean')
  ) {
    return null;
  }
  return {
    scorePause: v.scorePause,
    lockAfterStart: v.lockAfterStart,
    strictNick: v.strictNick,
    autoNext: v.autoNext ?? false,
  };
}
